import React, { useCallback, useEffect, useState } from "react";
import { Check, RefreshCw, Send } from "lucide-react";
import { api } from "../console/api";
import { Groupe, TitreGroupe, Vide } from "../espace/ui";
import { francs } from "./outils";

const GROUPES = ["impayes", "paiements_partiels", "impayes_saisons_passees"];

/* L'argent du semestre en trois chiffres, puis les familles en retard, à relancer d'un tap.
   La relance part par e-mail, comme depuis la console (un rappel par famille). */
export const Argent = ({ onCompte }) => {
  const [stats, setStats] = useState(null);
  const [notes, setNotes] = useState(null);
  const [choix, setChoix] = useState([]);
  const [message, setMessage] = useState("");
  const [envoi, setEnvoi] = useState(false);

  const charger = useCallback(() => {
    api("/stats").then(setStats).catch((e) => setMessage(e.message));
    api("/notifications").then(setNotes).catch((e) => setMessage(e.message));
  }, []);
  useEffect(charger, [charger]);

  const relancer = async (forcer = false) => {
    setEnvoi(true); setMessage("");
    try {
      const r = await api("/notifications/impayes/relancer", {
        method: "POST", body: JSON.stringify({ refs: choix, forcer }),
      });
      // Le serveur revérifie chaque famille : celles qui ne sont pas en retard sont écartées.
      const ignorees = r.ignorees || [];
      setMessage(`${r.envois.length} rappel${r.envois.length > 1 ? "s" : ""} envoyé${r.envois.length > 1 ? "s" : ""}.`
        + (ignorees.length ? ` ${ignorees.length} sans rappel : ${[...new Set(ignorees.map((i) => i.groupe || i.motif))].join(", ")}.` : "")
        + (r.avertissement ? ` Attention : ${r.avertissement}.` : ""));
      setChoix([]); charger(); onCompte?.();
    } catch (e) {
      // La console demande d'abord le relevé bancaire : on explique, et on laisse relancer
      // quand même en connaissance de cause.
      if (!forcer && /relev/i.test(e.message)
        && window.confirm(`${e.message}\n\nRelancer quand même ?`)) {
        setEnvoi(false);
        relancer(true);
        return;
      }
      setMessage(e.message);
    }
    setEnvoi(false);
  };

  const s = stats?.argent?.[`s${stats?.semestre_courant}`];
  const groupes = (notes?.groupes || []).filter((g) => GROUPES.includes(g.id));
  return (
    <div className="space-y-5" data-testid="ecole-argent">
      {s ? (
        <div className="rounded-[22px] p-5 text-white" style={{ background: "linear-gradient(140deg,#0F1B2D 0%,#123243 60%,#00A8AF 100%)" }}>
          <div className="text-[12px] uppercase tracking-[0.14em] font-semibold" style={{ color: "#66EAF0" }}>
            Semestre {stats.semestre_courant}
          </div>
          <div className="grid grid-cols-3 gap-2 mt-3">
            {[["Facturé", s.du], ["Encaissé", s.encaisse], ["Reste", s.reste]].map(([l, v]) => (
              <div key={l}>
                <div className="text-[18px] font-semibold tabular-nums leading-tight">{francs(v)}</div>
                <div className="text-[11.5px]" style={{ color: "rgba(255,255,255,0.7)" }}>{l}</div>
              </div>
            ))}
          </div>
          <div className="text-[12.5px] mt-3" style={{ color: "rgba(255,255,255,0.8)" }}>
            {s.impayees} facture{s.impayees > 1 ? "s" : ""} impayée{s.impayees > 1 ? "s" : ""}
            {s.a_envoyer ? ` · ${s.a_envoyer} à envoyer (sur ordinateur)` : ""}
          </div>
        </div>
      ) : <div className="py-10 grid place-items-center"><RefreshCw className="animate-spin" size={20} /></div>}

      {message && <div className="rounded-2xl px-4 py-3 text-sm bg-white" style={{ border: "1px solid var(--border)" }}>{message}</div>}

      {notes && !groupes.length && <Vide>Aucun retard de paiement.</Vide>}
      {groupes.map((g) => (
        <section key={g.id}>
          <TitreGroupe action={g.selection && (
            <button className="text-[13px] font-medium" style={{ color: "var(--primary)" }}
              onClick={() => setChoix((c) => (g.items.every((it) => c.includes(it.ref))
                ? c.filter((r) => !g.items.some((it) => it.ref === r))
                : [...new Set([...c, ...g.items.map((it) => it.ref)])]))}>
              Tout choisir
            </button>
          )}>{g.titre} ({g.nb})</TitreGroupe>
          <Groupe>
            {g.items.map((it) => {
              const pris = choix.includes(it.ref);
              return (
                <button key={it.ref} disabled={!g.selection}
                  onClick={() => setChoix((c) => (pris ? c.filter((r) => r !== it.ref) : [...c, it.ref]))}
                  className="w-full flex items-start gap-3 px-4 py-3 text-left active:bg-[#F1F5F9]" data-testid="ecole-impaye">
                  {g.selection && (
                    <span className="w-6 h-6 rounded-full grid place-items-center shrink-0 mt-0.5"
                      style={pris ? { background: "var(--primary)", color: "#fff" } : { border: "2px solid #CBD5E1" }}>
                      {pris && <Check size={14} strokeWidth={3} />}
                    </span>
                  )}
                  <span className="min-w-0">
                    <span className="block text-[15px] leading-snug">{it.titre}</span>
                    <span className="block text-[12.5px] text-[var(--muted)] mt-0.5">{it.detail}</span>
                  </span>
                </button>
              );
            })}
          </Groupe>
        </section>
      ))}

      {choix.length > 0 && (
        // Au-dessus de la barre d'onglets fixe (l'écran défile dessous) : collé à « bottom-2 »,
        // le bouton restait caché derrière elle (01.11.2026).
        <div className="sticky z-10" style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 92px)" }}>
          <button onClick={() => relancer(false)} disabled={envoi} className="btn-primary w-full justify-center shadow-lg"
            data-testid="ecole-relancer">
            <Send size={15} /> {envoi ? "Envoi…" : `Relancer ${choix.length} famille${choix.length > 1 ? "s" : ""}`}
          </button>
        </div>
      )}
    </div>
  );
};
